import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Form, Modal, Spinner } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useAuth from "../../../hooks/useAuth";
import { toastError, toastSuccess } from "../../../helpers/Toast";
import "../../../css/Scheduler.css";

const SHOW_TEST_DATA_TOOLS = false;

const eventTypeLabels = {
  WORK_START: "Rozpoczęcie pracy",
  WORK_END: "Zakończenie pracy",
  BREAK_START: "Początek przerwy",
  BREAK_END: "Koniec przerwy",
  ZONE_ENTRY: "Wejście do strefy",
  ZONE_EXIT: "Wyjście ze strefy"
};

const sourceLabels = {
  CARD: "Karta",
  PIN: "PIN",
  MANUAL: "Ręcznie",
  IMPORT: "Import",
  MOCK: "Dane testowe",
  SIMULATOR: "Symulator terminala",
  TERMINAL: "Terminal fizyczny",
  LEGACY: "Historia wcześniejsza"
};

const simulatorStateLabels = {
  OFF_WORK: "Poza pracą",
  WORKING: "W pracy",
  ON_BREAK: "Na przerwie"
};

const simulatorActions = [
  { type: "WORK_START", label: "Rozpocznij pracę", variant: "success" },
  { type: "BREAK_START", label: "Wyjdź na przerwę", variant: "warning" },
  { type: "BREAK_END", label: "Wróć z przerwy", variant: "info" },
  { type: "WORK_END", label: "Zakończ pracę", variant: "danger" }
];

const toDateString = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const toDateTimeLocal = date => `${toDateString(date)}T${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
const formatDateTime = value => value ? String(value).replace("T", " ").slice(0, 16) : "—";
const formatTime = value => value ? String(value).slice(11, 16) : "—";
const feedbackVariant = mood => ({ POSITIVE: "success", NEGATIVE: "danger", NEUTRAL: "secondary" }[mood] || "secondary");

const mockStartFor = today => {
  const year = today.getMonth() >= 6 ? today.getFullYear() : today.getFullYear() - 1;
  return `${year}-07-01`;
};

const RcpEvents = () => {
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const userRoles = Array.isArray(auth?.roles)
    ? auth.roles
    : typeof auth?.roles === "string"
      ? auth.roles.replace(/[\[\]\s]/g, "").split(",")
      : [];
  const isAdmin = userRoles.includes("ROLE_ADMIN");

  const today = useMemo(() => new Date(), []);
  const weekAgo = useMemo(() => {
    const date = new Date(today);
    date.setDate(date.getDate() - 7);
    return date;
  }, [today]);

  const [events, setEvents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [cards, setCards] = useState([]);
  const [locations, setLocations] = useState([]);
  const [zones, setZones] = useState([]);
  const [terminals, setTerminals] = useState([]);
  const [complianceAlerts, setComplianceAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showMockForm, setShowMockForm] = useState(false);
  const [showSimulator, setShowSimulator] = useState(false);
  const [simulatorLoading, setSimulatorLoading] = useState(false);
  const [simulatorState, setSimulatorState] = useState(null);
  const [simulatorFeedback, setSimulatorFeedback] = useState(null);

  const [filters, setFilters] = useState({
    employeeId: "",
    dateFrom: toDateString(weekAgo),
    dateTo: toDateString(today)
  });

  const [form, setForm] = useState({
    cardId: "",
    eventType: "WORK_START",
    occurredAt: toDateTimeLocal(new Date()),
    locationId: "",
    notes: ""
  });

  const [simulatorForm, setSimulatorForm] = useState({
    employeeId: "",
    occurredAt: toDateTimeLocal(new Date()),
    locationId: "",
    notes: "",
    linkToSchedule: true
  });

  const [mockForm, setMockForm] = useState({
    employeeId: "",
    dateFrom: mockStartFor(today),
    dateTo: toDateString(today),
    includeUnscheduledHistory: true,
    replaceExistingMock: true
  });

  const employeeName = id => {
    const employee = employees.find(item => Number(item.id) === Number(id));
    return employee ? `${employee.firstName ?? ""} ${employee.lastName ?? ""}`.trim() : `ID ${id}`;
  };

  const locationName = id => {
    if (id == null) return "";
    const location = locations.find(item => Number(item.id) === Number(id));
    return location?.name ?? `ID ${id}`;
  };

  const zoneName = id => id == null ? "" : (zones.find(item => Number(item.id) === Number(id))?.name ?? `ID ${id}`);
  const terminalName = id => id == null ? "" : (terminals.find(item => Number(item.id) === Number(id))?.name ?? `ID ${id}`);

  const loadCompliance = async nextFilters => {
    const params = {dateFrom: nextFilters.dateFrom, dateTo: nextFilters.dateTo};
    if (nextFilters.employeeId) params.employeeId = Number(nextFilters.employeeId);
    const response = await axiosPrivate.get("/rcp-events/compliance", {params});
    setComplianceAlerts(response.data || []);
  };

  const downloadPhoto = async item => {
    try {
      const response = await axiosPrivate.get(`/rcp-events/${item.id}/photo`, {responseType: "blob"});
      const url = URL.createObjectURL(response.data);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (error) { toastError(error?.response?.data?.message || "Nie udało się pobrać zdjęcia RCP."); }
  };

  const loadEvents = async nextFilters => {
    const params = {};
    if (nextFilters.employeeId) params.employeeId = Number(nextFilters.employeeId);
    if (nextFilters.dateFrom) params.dateFrom = nextFilters.dateFrom;
    if (nextFilters.dateTo) params.dateTo = nextFilters.dateTo;
    const [response] = await Promise.all([
      axiosPrivate.get("/rcp-events", { params }),
      loadCompliance(nextFilters)
    ]);
    setEvents(response.data ?? []);
  };

  const loadSimulatorState = async (employeeId, occurredAt) => {
    if (!employeeId || !occurredAt) {
      setSimulatorState(null);
      return;
    }
    setSimulatorLoading(true);
    try {
      const response = await axiosPrivate.get(`/rcp-events/simulator/state/${Number(employeeId)}`, {
        params: { at: occurredAt }
      });
      setSimulatorState(response.data ?? null);
    } catch (error) {
      setSimulatorState(null);
      toastError(error?.response?.data?.message || "Nie udało się pobrać stanu symulatora RCP.");
    } finally {
      setSimulatorLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const [eventsResponse, referenceResponse, zonesResponse, terminalsResponse, complianceResponse] = await Promise.all([
          axiosPrivate.get("/rcp-events", { params: { dateFrom: filters.dateFrom, dateTo: filters.dateTo } }),
          axiosPrivate.get("/rcp-events/reference-data"),
          axiosPrivate.get("/zones"),
          axiosPrivate.get("/rcp-terminals"),
          axiosPrivate.get("/rcp-events/compliance", {params: {dateFrom: filters.dateFrom, dateTo: filters.dateTo}})
        ]);
        if (!mounted) return;
        setEvents(eventsResponse.data ?? []);
        setEmployees(referenceResponse.data?.employees ?? []);
        setCards(referenceResponse.data?.cards ?? []);
        setLocations(referenceResponse.data?.locations ?? []);
        setZones(zonesResponse.data ?? []);
        setTerminals(terminalsResponse.data ?? []);
        setComplianceAlerts(complianceResponse.data ?? []);
      } catch (error) {
        toastError(error?.response?.data?.message || "Nie udało się pobrać rejestru RCP.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const employeeId = searchParams.get("simulateEmployeeId");
    if (!employeeId || employees.length === 0) return;
    const exists = employees.some(item => Number(item.id) === Number(employeeId));
    if (!exists) return;

    const requestedAt = searchParams.get("simulateAt");
    const occurredAt = requestedAt && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(requestedAt)
      ? requestedAt.slice(0, 16)
      : toDateTimeLocal(new Date());

    setSimulatorForm(current => ({ ...current, employeeId: String(employeeId), occurredAt }));
    setSimulatorFeedback(null);
    setShowSimulator(true);
    const next = new URLSearchParams(searchParams);
    next.delete("simulateEmployeeId");
    next.delete("simulateAt");
    setSearchParams(next, { replace: true });
  }, [employees, searchParams, setSearchParams]);

  useEffect(() => {
    if (!showSimulator || !simulatorForm.employeeId || !simulatorForm.occurredAt) return;
    const handle = window.setTimeout(() => {
      loadSimulatorState(simulatorForm.employeeId, simulatorForm.occurredAt);
    }, 200);
    return () => window.clearTimeout(handle);
  }, [showSimulator, simulatorForm.employeeId, simulatorForm.occurredAt]);

  const applyFilters = async event => {
    event.preventDefault();
    setLoading(true);
    try {
      await loadEvents(filters);
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się pobrać rejestru RCP.");
    } finally {
      setLoading(false);
    }
  };

  const openForm = () => {
    setForm({ cardId: "", eventType: "WORK_START", occurredAt: toDateTimeLocal(new Date()), locationId: "", notes: "" });
    setShowForm(true);
  };

  const openSimulator = () => {
    setSimulatorState(null);
    setSimulatorFeedback(null);
    setSimulatorForm({
      employeeId: filters.employeeId || "",
      occurredAt: toDateTimeLocal(new Date()),
      locationId: "",
      notes: "",
      linkToSchedule: true
    });
    setShowSimulator(true);
  };

  const openMockForm = () => {
    setMockForm(current => ({ ...current, dateTo: toDateString(new Date()) }));
    setShowMockForm(true);
  };

  const saveEvent = async event => {
    event.preventDefault();
    if (!form.cardId || !form.eventType || !form.occurredAt) {
      toastError("Wybierz kartę, typ zdarzenia i czas.");
      return;
    }
    setSaving(true);
    try {
      await axiosPrivate.post("/rcp-events/register", {
        cardId: form.cardId,
        eventType: form.eventType,
        occurredAt: form.occurredAt,
        locationId: form.locationId ? Number(form.locationId) : null,
        source: "MANUAL",
        notes: form.notes || null
      });
      await loadEvents(filters);
      setShowForm(false);
      toastSuccess("Zapisano zdarzenie RCP.");
    } catch (error) {
      toastError(error?.response?.data?.message || error?.response?.data || "Nie udało się zapisać zdarzenia RCP.");
    } finally {
      setSaving(false);
    }
  };

  const simulate = async eventType => {
    if (!simulatorForm.employeeId || !simulatorForm.occurredAt) {
      toastError("Wybierz pracownika i czas odbicia.");
      return;
    }
    setSaving(true);
    try {
      const response = await axiosPrivate.post("/rcp-events/simulator/register-with-feedback", {
        employeeId: Number(simulatorForm.employeeId),
        eventType,
        occurredAt: simulatorForm.occurredAt,
        locationId: simulatorForm.locationId ? Number(simulatorForm.locationId) : null,
        notes: simulatorForm.notes || null,
        linkToSchedule: simulatorForm.linkToSchedule
      });
      setSimulatorFeedback(response.data?.feedback ?? null);

      const simulatedDate = simulatorForm.occurredAt.slice(0, 10);
      const nextFilters = {
        employeeId: simulatorForm.employeeId,
        dateFrom: filters.dateFrom > simulatedDate ? simulatedDate : filters.dateFrom,
        dateTo: filters.dateTo < simulatedDate ? simulatedDate : filters.dateTo
      };
      setFilters(nextFilters);
      setSimulatorForm(current => ({ ...current, notes: "" }));
      await Promise.all([
        loadEvents(nextFilters),
        loadSimulatorState(simulatorForm.employeeId, simulatorForm.occurredAt)
      ]);
      toastSuccess(`Symulator zapisał: ${eventTypeLabels[eventType]}.`);
    } catch (error) {
      toastError(error?.response?.data?.message || error?.response?.data || "Nie udało się zapisać odbicia w symulatorze RCP.");
    } finally {
      setSaving(false);
    }
  };

  const generateMock = async event => {
    event.preventDefault();
    if (!mockForm.dateFrom || !mockForm.dateTo) {
      toastError("Podaj zakres danych testowych.");
      return;
    }
    setSaving(true);
    try {
      const response = await axiosPrivate.post("/rcp-events/mock/generate", {
        dateFrom: mockForm.dateFrom,
        dateTo: mockForm.dateTo,
        employeeId: mockForm.employeeId ? Number(mockForm.employeeId) : null,
        includeUnscheduledHistory: mockForm.includeUnscheduledHistory,
        replaceExistingMock: mockForm.replaceExistingMock
      });
      const result = response.data ?? {};
      const nextFilters = {
        employeeId: mockForm.employeeId,
        dateFrom: result.dateFrom ?? mockForm.dateFrom,
        dateTo: result.dateTo ?? mockForm.dateTo
      };
      setFilters(nextFilters);
      await loadEvents(nextFilters);
      setShowMockForm(false);
      toastSuccess(`Wygenerowano ${result.generatedEvents ?? 0} zdarzeń RCP w ${Number(result.scheduleBasedSessions ?? 0) + Number(result.unscheduledSessions ?? 0)} sesjach. Braki RCP: ${result.missingSessions ?? 0}.`);
    } catch (error) {
      toastError(error?.response?.data?.message || error?.response?.data || "Nie udało się wygenerować danych testowych RCP.");
    } finally {
      setSaving(false);
    }
  };

  const deleteMock = async () => {
    if (!mockForm.dateFrom || !mockForm.dateTo) {
      toastError("Podaj zakres danych testowych.");
      return;
    }
    if (!window.confirm(`Usunąć dane testowe RCP z okresu ${mockForm.dateFrom} – ${mockForm.dateTo}?`)) return;
    setSaving(true);
    try {
      const params = { dateFrom: mockForm.dateFrom, dateTo: mockForm.dateTo };
      if (mockForm.employeeId) params.employeeId = Number(mockForm.employeeId);
      const response = await axiosPrivate.delete("/rcp-events/mock", { params });
      await loadEvents(filters);
      toastSuccess(`Usunięto ${response.data?.deletedMockEvents ?? 0} testowych zdarzeń RCP.`);
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się usunąć danych testowych RCP.");
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async id => {
    if (!window.confirm("Usunąć to zdarzenie RCP?")) return;
    try {
      await axiosPrivate.delete(`/rcp-events/${id}`);
      await loadEvents(filters);
      if (showSimulator && simulatorForm.employeeId) {
        await loadSimulatorState(simulatorForm.employeeId, simulatorForm.occurredAt);
      }
      toastSuccess("Usunięto zdarzenie RCP.");
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się usunąć zdarzenia RCP.");
    }
  };

  const summary = useMemo(() => events.reduce((result, item) => {
    result[item.eventType] = (result[item.eventType] ?? 0) + 1;
    return result;
  }, {}), [events]);

  const mockCount = useMemo(() => events.filter(item => item.source === "MOCK").length, [events]);
  const simulatorCount = useMemo(() => events.filter(item => item.source === "SIMULATOR").length, [events]);
  const allowedEvents = simulatorState?.allowedEventTypes ?? [];
  const plannedShift = simulatorState?.plannedShift;

  return (
    <div id="layoutSidenav_content">
      <div className="app-component-card-header shadow d-flex justify-content-between align-items-center">
        <div>Rejestr RCP</div>
        <div className="d-flex gap-2">
          {SHOW_TEST_DATA_TOOLS && isAdmin && <Button variant="outline-secondary" onClick={openMockForm}>Dane testowe</Button>}
          {SHOW_TEST_DATA_TOOLS && <Button variant="outline-primary" onClick={openSimulator}>Symulator odbicia</Button>}
          <Button variant="primary" onClick={openForm}>Dodaj zdarzenie</Button>
        </div>
      </div>

      <div className="container-fluid px-4 py-3">
        <form className="rcp-filter-bar" onSubmit={applyFilters}>
          <Form.Group>
            <Form.Label>Pracownik</Form.Label>
            <Form.Select value={filters.employeeId} onChange={event => setFilters(prev => ({ ...prev, employeeId: event.target.value }))}>
              <option value="">Wszyscy</option>
              {employees.map(employee => <option key={employee.id} value={employee.id}>{employeeName(employee.id)}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Od</Form.Label>
            <Form.Control type="date" value={filters.dateFrom} onChange={event => setFilters(prev => ({ ...prev, dateFrom: event.target.value }))}/>
          </Form.Group>
          <Form.Group>
            <Form.Label>Do</Form.Label>
            <Form.Control type="date" value={filters.dateTo} onChange={event => setFilters(prev => ({ ...prev, dateTo: event.target.value }))}/>
          </Form.Group>
          <Button type="submit" variant="outline-primary" disabled={loading}>{loading ? "Ładowanie..." : "Pokaż"}</Button>
        </form>

        <div className="d-flex flex-wrap gap-3 mb-3 small text-muted">
          <span>Rozpoczęcia: <b>{summary.WORK_START ?? 0}</b></span>
          <span>Zakończenia: <b>{summary.WORK_END ?? 0}</b></span>
          <span>Początki przerw: <b>{summary.BREAK_START ?? 0}</b></span>
          <span>Końce przerw: <b>{summary.BREAK_END ?? 0}</b></span>
          {mockCount > 0 && <span>Dane MOCK: <b>{mockCount}</b></span>}
          {simulatorCount > 0 && <span>Symulator: <b>{simulatorCount}</b></span>}
        </div>

        {(mockCount > 0 || simulatorCount > 0) && (
          <Alert variant="warning" className="py-2">
            W tym zakresie znajdują się dane testowe. Zdarzenia <b>MOCK</b> są generowane hurtowo, a <b>SIMULATOR</b> oznacza pojedyncze odbicia wykonane przez symulator terminala.
          </Alert>
        )}

        {complianceAlerts.length > 0 && (
          <Alert variant="danger">
            <strong>Alerty zgodności RCP: {complianceAlerts.length}</strong>
            <ul className="mb-0 mt-2">{complianceAlerts.slice(0, 8).map((alert, index) => <li key={`${alert.type}-${alert.eventId || index}-${alert.startedAt || ""}`}>{employeeName(alert.employeeId)} — {alert.message}{alert.minutes != null ? ` (${alert.minutes} min)` : ""}</li>)}</ul>
            {complianceAlerts.length > 8 && <small>oraz {complianceAlerts.length - 8} kolejnych alertów w wybranym zakresie</small>}
          </Alert>
        )}

        <div className="card shadow-sm">
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0 rcp-events-table">
              <thead>
                <tr><th>Czas</th><th>Pracownik</th><th>Zdarzenie</th><th>Karta</th><th>Lokalizacja</th><th>Terminal / strefa</th><th>Kontrola</th><th>Źródło</th><th>Uwagi</th><th></th></tr>
              </thead>
              <tbody>
                {events.length === 0 ? (
                  <tr><td colSpan="10" className="text-center py-4">Brak zdarzeń RCP w wybranym okresie.</td></tr>
                ) : events.map(item => (
                  <tr key={item.id}>
                    <td>{formatDateTime(item.occurredAt)}</td>
                    <td>{employeeName(item.employeeId)}</td>
                    <td><span className={`rcp-event-type rcp-event-type-${String(item.eventType || "").toLowerCase()}`}>{eventTypeLabels[item.eventType] ?? item.eventType}</span></td>
                    <td>{item.cardId || item.timeCardId || ""}</td>
                    <td>{locationName(item.locationId)}</td>
                    <td>{item.terminalId ? <><div>{terminalName(item.terminalId)}</div><small>{zoneName(item.zoneId) || "bez strefy"}</small></> : "—"}</td>
                    <td>{item.zoneValid === false ? <span className="text-danger" title={item.zoneValidationMessage || "Niewłaściwa strefa"}>NIEWŁAŚCIWA STREFA</span> : item.zoneValid === true ? <span className="text-success">strefa OK</span> : "—"}{item.photoAvailable && <div><Button size="sm" variant="link" className="p-0" onClick={() => downloadPhoto(item)}>Zdjęcie</Button></div>}{item.photoRequired && !item.photoAvailable && <div className="text-danger">brak wymaganego zdjęcia</div>}</td>
                    <td>{sourceLabels[item.source] ?? item.source}</td>
                    <td>{item.notes || ""}</td>
                    <td><Button size="sm" variant="outline-danger" onClick={() => deleteEvent(item.id)}>Usuń</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Modal show={SHOW_TEST_DATA_TOOLS && showSimulator} onHide={() => setShowSimulator(false)} centered backdrop="static" size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Symulator terminala RCP</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="py-2">
            Symulator zapisuje normalne zdarzenia RCP ze źródłem <b>SIMULATOR</b>. Ten sam rejestr jest później używany do rozliczenia grafiku, czasu pracy i raportów.
          </Alert>

          <div className="row g-3 mb-3">
            <Form.Group className="col-md-5">
              <Form.Label>Pracownik</Form.Label>
              <Form.Select value={simulatorForm.employeeId} onChange={event => setSimulatorForm(prev => ({ ...prev, employeeId: event.target.value }))}>
                <option value="">-- wybierz --</option>
                {employees.map(employee => <option key={employee.id} value={employee.id}>{employeeName(employee.id)}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="col-md-4">
              <Form.Label>Data i godzina odbicia</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control type="datetime-local" value={simulatorForm.occurredAt} onChange={event => setSimulatorForm(prev => ({ ...prev, occurredAt: event.target.value }))}/>
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={() => setSimulatorForm(prev => ({ ...prev, occurredAt: toDateTimeLocal(new Date()) }))}
                >
                  Teraz
                </Button>
              </div>
            </Form.Group>
            <Form.Group className="col-md-3">
              <Form.Label>Lokalizacja</Form.Label>
              <Form.Select value={simulatorForm.locationId} onChange={event => setSimulatorForm(prev => ({ ...prev, locationId: event.target.value }))}>
                <option value="">Brak</option>
                {locations.map(location => <option key={location.id} value={location.id}>{location.name}</option>)}
              </Form.Select>
            </Form.Group>
          </div>

          <Form.Check
            className="mb-3"
            type="checkbox"
            checked={simulatorForm.linkToSchedule}
            onChange={event => setSimulatorForm(prev => ({ ...prev, linkToSchedule: event.target.checked }))}
            label="Powiąż ze zmianą z grafiku (jeżeli dla tej daty istnieje pasująca zmiana)"
          />

          {!simulatorForm.employeeId ? (
            <Alert variant="secondary">Wybierz pracownika, aby odczytać stan terminala i dostępne akcje.</Alert>
          ) : simulatorLoading ? (
            <div className="d-flex justify-content-center py-4"><Spinner animation="border" size="sm" className="me-2"/> Odczyt stanu RCP...</div>
          ) : simulatorState ? (
            <>
              <div className="card mb-3">
                <div className="card-body py-3">
                  <div className="d-flex flex-wrap justify-content-between gap-3 align-items-center">
                    <div>
                      <div className="text-muted small">Aktualny stan w wybranej chwili</div>
                      <div className="fs-5 fw-bold">{simulatorStateLabels[simulatorState.state] ?? simulatorState.state}</div>
                    </div>
                    <div>
                      <div className="text-muted small">Karta pracownika</div>
                      <div className="fw-semibold">{simulatorState.cardId || "Brak aktywnej karty"}</div>
                    </div>
                    <div>
                      <div className="text-muted small">Czas symulacji</div>
                      <div className="fw-semibold">{formatDateTime(simulatorState.at)}</div>
                    </div>
                  </div>
                </div>
              </div>


              {simulatorFeedback && (
                <Alert variant={feedbackVariant(simulatorFeedback.mood)} className="d-flex align-items-center gap-3">
                  <div style={{fontSize: "2.4rem", lineHeight: 1}} aria-label={simulatorFeedback.mood}>{simulatorFeedback.emoticon || "😐"}</div>
                  <div>
                    <div className="fw-semibold">Informacja zwrotna terminala</div>
                    <div>{simulatorFeedback.message}</div>
                    <div className="small mt-1">Sugerowana zmiana punktów: {simulatorFeedback.suggestedPointsDelta > 0 ? "+" : ""}{simulatorFeedback.suggestedPointsDelta ?? 0}</div>
                  </div>
                </Alert>
              )}

              {plannedShift ? (
                <Alert variant={plannedShift.atWithinShift ? "success" : "secondary"}>
                  <div className="fw-semibold">Najbliższa zmiana z grafiku</div>
                  <div>
                    {formatTime(plannedShift.startsAt)}–{formatTime(plannedShift.endsAt)}
                    {plannedShift.positionName ? ` · ${plannedShift.positionName}` : ""}
                    {plannedShift.scheduleName ? ` · ${plannedShift.scheduleName}` : ""}
                  </div>
                  {!plannedShift.atWithinShift && <div className="small mt-1">Wybrana godzina leży poza godzinami zmiany. Zdarzenie nadal można zapisać.</div>}
                </Alert>
              ) : (
                <Alert variant="warning">
                  Pracownik nie ma zmiany w grafiku tego dnia. Odbicie zostanie zapisane jako RCP bez odpowiadającej zmiany i będzie widoczne w raportach jako czas bez grafiku.
                </Alert>
              )}

              <div className="d-grid gap-2 d-md-flex mb-3">
                {simulatorActions.map(action => (
                  <Button
                    key={action.type}
                    variant={allowedEvents.includes(action.type) ? action.variant : `outline-${action.variant}`}
                    disabled={saving || !allowedEvents.includes(action.type)}
                    onClick={() => simulate(action.type)}
                  >
                    {action.label}
                  </Button>
                ))}
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Uwagi do kolejnego odbicia</Form.Label>
                <Form.Control as="textarea" rows={2} value={simulatorForm.notes} onChange={event => setSimulatorForm(prev => ({ ...prev, notes: event.target.value }))}/>
              </Form.Group>

              <div className="card">
                <div className="card-header py-2 d-flex justify-content-between align-items-center">
                  <span>Ostatnie odbicia tego dnia do wybranej godziny</span>
                  <Badge bg="secondary">{simulatorState.recentEvents?.length ?? 0}</Badge>
                </div>
                <div className="table-responsive">
                  <table className="table table-sm mb-0 align-middle">
                    <thead><tr><th>Godzina</th><th>Zdarzenie</th><th>Źródło</th><th>Uwagi</th></tr></thead>
                    <tbody>
                      {(simulatorState.recentEvents ?? []).length === 0 ? (
                        <tr><td colSpan="4" className="text-center text-muted py-3">Brak wcześniejszych odbić.</td></tr>
                      ) : (simulatorState.recentEvents ?? []).map(item => (
                        <tr key={item.id}>
                          <td>{formatTime(item.occurredAt)}</td>
                          <td>{eventTypeLabels[item.eventType] ?? item.eventType}</td>
                          <td>{sourceLabels[item.source] ?? item.source}</td>
                          <td>{item.notes || ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSimulator(false)} disabled={saving}>Zamknij</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showForm} onHide={() => setShowForm(false)} centered backdrop="static">
        <Modal.Header closeButton><Modal.Title>Nowe zdarzenie RCP</Modal.Title></Modal.Header>
        <Form onSubmit={saveEvent}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Karta RCP</Form.Label>
              <Form.Select value={form.cardId} onChange={event => setForm(prev => ({ ...prev, cardId: event.target.value }))} required>
                <option value="">-- wybierz --</option>
                {cards.filter(card => card.active && card.valid && card.employeeId != null).map(card => (
                  <option key={card.id} value={card.cardId}>{card.cardId} · {employeeName(card.employeeId)}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Typ zdarzenia</Form.Label>
              <Form.Select value={form.eventType} onChange={event => setForm(prev => ({ ...prev, eventType: event.target.value }))}>
                {Object.entries(eventTypeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Data i godzina</Form.Label>
              <Form.Control type="datetime-local" value={form.occurredAt} onChange={event => setForm(prev => ({ ...prev, occurredAt: event.target.value }))} required/>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Lokalizacja</Form.Label>
              <Form.Select value={form.locationId} onChange={event => setForm(prev => ({ ...prev, locationId: event.target.value }))}>
                <option value="">Brak</option>
                {locations.map(location => <option key={location.id} value={location.id}>{location.name}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Uwagi</Form.Label>
              <Form.Control as="textarea" rows={2} value={form.notes} onChange={event => setForm(prev => ({ ...prev, notes: event.target.value }))}/>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowForm(false)} disabled={saving}>Anuluj</Button>
            <Button type="submit" variant="primary" disabled={saving}>{saving ? "Zapisywanie..." : "Zapisz"}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={SHOW_TEST_DATA_TOOLS && showMockForm} onHide={() => setShowMockForm(false)} centered backdrop="static" size="lg">
        <Modal.Header closeButton><Modal.Title>Testowa historia RCP</Modal.Title></Modal.Header>
        <Form onSubmit={generateMock}>
          <Modal.Body>
            <Alert variant="info">
              Generator tworzy fikcyjne odbicia na podstawie istniejących grafików. Dla aktywnych zatrudnionych pracowników, którzy nie mają żadnych zmian w wybranym okresie, może dodatkowo utworzyć samą historię RCP. Nie nadpisuje prawdziwych zdarzeń ani starej historii czasu pracy.
            </Alert>
            <div className="row g-3">
              <Form.Group className="col-md-6">
                <Form.Label>Pracownik</Form.Label>
                <Form.Select value={mockForm.employeeId} onChange={event => setMockForm(prev => ({ ...prev, employeeId: event.target.value }))}>
                  <option value="">Wszyscy aktywni zatrudnieni</option>
                  {employees.map(employee => <option key={employee.id} value={employee.id}>{employeeName(employee.id)}</option>)}
                </Form.Select>
              </Form.Group>
              <Form.Group className="col-md-3">
                <Form.Label>Od</Form.Label>
                <Form.Control type="date" value={mockForm.dateFrom} onChange={event => setMockForm(prev => ({ ...prev, dateFrom: event.target.value }))} required/>
              </Form.Group>
              <Form.Group className="col-md-3">
                <Form.Label>Do</Form.Label>
                <Form.Control type="date" value={mockForm.dateTo} max={toDateString(today)} onChange={event => setMockForm(prev => ({ ...prev, dateTo: event.target.value }))} required/>
              </Form.Group>
            </div>
            <Form.Check
              className="mt-3"
              type="checkbox"
              checked={mockForm.includeUnscheduledHistory}
              onChange={event => setMockForm(prev => ({ ...prev, includeUnscheduledHistory: event.target.checked }))}
              label="Utwórz historię także dla pracowników bez grafiku w tym okresie"
            />
            <Form.Check
              className="mt-2"
              type="checkbox"
              checked={mockForm.replaceExistingMock}
              onChange={event => setMockForm(prev => ({ ...prev, replaceExistingMock: event.target.checked }))}
              label="Przed generowaniem usuń wcześniejsze dane testowe z tego zakresu"
            />
            <div className="small text-muted mt-3">
              Generator celowo tworzy normalne dni, spóźnienia, wcześniejsze wyjścia, nadgodziny, brak rozpoczęcia pracy, brak zakończenia oraz przerwy. Wynik jest deterministyczny, więc ponowne wygenerowanie tego samego zakresu daje porównywalne dane.
            </div>
          </Modal.Body>
          <Modal.Footer className="justify-content-between">
            <Button type="button" variant="outline-danger" onClick={deleteMock} disabled={saving}>Usuń MOCK z okresu</Button>
            <div className="d-flex gap-2">
              <Button type="button" variant="secondary" onClick={() => setShowMockForm(false)} disabled={saving}>Anuluj</Button>
              <Button type="submit" variant="primary" disabled={saving}>{saving ? "Generowanie..." : "Generuj historię"}</Button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default RcpEvents;


